/**
 * Protected Route Component
 * Server-side authentication verification for admin routes
 * 
 * SECURITY: This component validates the token with the backend
 * before rendering protected content. This is CRITICAL for security.
 */
import React, { useState, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Loader2 } from 'lucide-react';

const API_BASE = window.location.origin || '';

const ProtectedRoute = ({ children }) => {
  const [authState, setAuthState] = useState('loading'); // 'loading' | 'authenticated' | 'unauthenticated'
  const location = useLocation();

  useEffect(() => {
    const verifyAuth = async () => {
      const token = localStorage.getItem('admin_token') || localStorage.getItem('vipgez_admin_token');
      
      if (!token) {
        setAuthState('unauthenticated');
        return;
      }

      try {
        // SECURITY: Verify token with backend - don't trust client-side storage alone
        const response = await fetch(`${API_BASE}/api/admin/profile`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          setAuthState('authenticated');
        } else {
          // Token invalid or expired - clear storage
          localStorage.removeItem('admin_token');
          localStorage.removeItem('vipgez_admin_token');
          setAuthState('unauthenticated');
        }
      } catch (error) {
        console.error('Auth verification failed:', error);
        setAuthState('unauthenticated');
      }
    };

    verifyAuth();
  }, [location.pathname]);

  if (authState === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-orange-500 mx-auto" />
          <p className="mt-2 text-gray-600">Verifying authentication...</p>
        </div>
      </div>
    );
  }

  if (authState === 'unauthenticated') {
    // Redirect to login with return URL
    return <Navigate to="/login-goturkeytransfer" state={{ from: location }} replace />;
  }

  return children;
};

export default ProtectedRoute;
